import { useState, useEffect, useCallback, useRef } from 'react';

import { useSelector } from 'react-redux';

import { getUserStorageKey } from '@/lib/storage/localStorage';
import { selectUserId } from '@/redux/user/selectors';

/**
 * Looks for a value in localStorage, if it doesn't exist, it will use the initialValue
 * The value is stored in localStorage under the key `moveo_${userId}` which means
 * that the user needs to be in Redux
 */
type SetValue<T> = T | ((prev: T) => T);

function useLocalStorage<UserPreferenceType>(
  property: string | undefined,
  initialValue: UserPreferenceType
): [UserPreferenceType, (value: SetValue<UserPreferenceType>) => void] {
  // we need user to be in Redux
  const userId = useSelector(selectUserId);
  const key = getUserStorageKey(userId);

  // Ref-stabilise initialValue so callers can pass object/array literals
  // (e.g. {} or []) without triggering infinite re-render loops.
  const initialValueRef = useRef(initialValue);

  const readLocal = useCallback(() => {
    if (typeof window === 'undefined' || !property) {
      return {};
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : {};
    } catch {
      return {};
    }
  }, [key, property]);

  const [storedValue, setStoredValue] = useState<UserPreferenceType>(
    property ? (readLocal()[property] ?? initialValue) : initialValue
  );

  useEffect(() => {
    if (property) {
      setStoredValue(readLocal()[property] ?? initialValueRef.current);
    }
  }, [property, readLocal]);

  const setValue = useCallback(
    (value: SetValue<UserPreferenceType>) => {
      if (!property) {
        return;
      }
      const userPref = readLocal();
      const currentValue = (userPref[property] ?? initialValueRef.current) as UserPreferenceType;
      const valueToStore =
        typeof value === 'function'
          ? (value as (prev: UserPreferenceType) => UserPreferenceType)(currentValue)
          : value;
      const newPref = { ...userPref, [property]: valueToStore };
      setStoredValue(valueToStore);

      window.localStorage.setItem(key, JSON.stringify(newPref));
    },
    [key, property, readLocal]
  );

  return [storedValue, setValue];
}

export default useLocalStorage;
